<script setup>
import * as d3 from 'd3';
import { computed, onMounted, ref, toRefs, watch } from 'vue';

import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';

import { coordinateTransformation } from './coordinateTransformation';

const props = defineProps({
  id: { type: String, required: true },
  currentImageUid: { type: String, required: true },
  canvasWidth: { type: Number, required: true },
  canvasHeight: { type: Number, required: true },
});

// Used to get current viewer:
const {
  id: viewID,
  currentImageUid: curImageID,
  canvasWidth,
  canvasHeight,
} = toRefs(props);
const viewportStateStore = useViewportStateStore();

const overlay = ref(null);

const showGridOverlay = computed(() => {
  return viewportStateStore.viewportStates[viewID.value]?.showGrid;
});

const { scaleFactor, overlayTransform, imageToScreenMatrix, updateLeftCornerCoords, curImageMetadata }
  = coordinateTransformation({
    currentImageUid: curImageID,
    id: viewID,
    canvasWidth,
    canvasHeight,
  });

function applyTransform() {
  if (!overlay.value) return;
  const stroke = 1 / scaleFactor.value;
  d3.select(overlay.value)
    .selectAll('path')
    .attr('transform', overlayTransform.value) // was scale(scaleFactor)
    .attr('stroke-width', stroke);
}

function createOverlay() {
  if (!overlay.value) return;

  if (!curImageMetadata.value) {
    return;
  }

  const [bottomX, bottomY] = curImageMetadata.value.dimensions;

  const overlaySel = d3.select(overlay.value);
  overlaySel.selectAll('svg').remove();

  if (!showGridOverlay.value) return;

  const svg = overlaySel
    .append('svg')
    .attr('width', '100%')
    .attr('height', '100%')
    .style('position', 'absolute')
    .style('top', '0')
    .style('left', '0')
    .style('overflow', 'visible');

  const gridGroup = svg
    .append('g')
    .attr('class', 'grid-group')
    .style('transform-origin', '0 0');

  // Create grid lines using a single path element
  let gridPath = '';

  // Vertical lines
  for (let x = 0; x <= bottomX; x++) {
    gridPath += `M ${x} 0 L ${x} ${bottomY} `;
  }

  // Horizontal lines
  for (let y = 0; y <= bottomY; y++) {
    gridPath += `M 0 ${y} L ${bottomX} ${y} `;
  }

  // Add the grid lines as a single path
  gridGroup
    .append('path')
    .attr('d', gridPath)
    .attr('stroke', 'white')
    .attr('stroke-width', 0.1)
    .attr('opacity', '0.5')
    .attr('fill', 'none');

  applyTransform();
}

const matrixReady = computed(() => {
  const m = imageToScreenMatrix.value;
  return !(m.a === 1 && m.b === 0 && m.c === 0 && m.d === 1 && m.e === 0 && m.f === 0);
});

watch(
  [showGridOverlay, curImageID],
  () => {
    updateLeftCornerCoords();
    createOverlay();
  },
  { immediate: true },
);

watch(
  () => overlayTransform.value,
  () => { if (matrixReady.value) requestAnimationFrame(applyTransform); },
  { immediate: true },
);

onMounted(() => {
  updateLeftCornerCoords();
  createOverlay();
});
</script>

<script>
export default {
  name: 'UIDDisplayGrid',
};
</script>

<template>
  <div class="overlay">
    <div
      ref="overlay" style="will-change: transform" :style="{
        position: 'absolute',
        left: '0px',
        top: '0px',
        width: '100%',
        height: '100%',
      }"
    />
  </div>
</template>

<style scoped>
.overlay {
  position: absolute;
  pointer-events: none;
  z-index: 0;
  width: 100%;
  height: 100%;
}
</style>
